import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { buildProductUrl } from '../utils/navigation';
import { addProductToCart, getProductRecommendationsV2 } from './useSupabase';
import { useCartStore } from '../store/cartStore';
import { useAuth } from './useAuth';
import { getPrimaryProductImageUrl } from '../utils/imageUtils';
import { getUserWishlistV2, deleteUserWishlistV2, useWishlist } from './useWishlist';
import type { Product, VariantSku } from '../components/ProductDetail/types';
import { useWishlistContext } from './useWishlistContext';

export const useProductDetail = () => {
  const { addToWishlist } = useWishlist();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Extract numeric id from slug like "chanel-oud-royal-1388" → "1388"
  const productId = (() => {
    if (!slug) return undefined;
    const parts = slug.split("-");
    const last = parts[parts.length - 1];
    return /^\d+$/.test(last) ? last : slug;
  })();
  const { userId, user } = useAuth();
  const addToCartStore = useCartStore((s) => s.addToCart);

  // State for product data
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for wishlist
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { refreshWishlist } = useWishlistContext();

  const dbSyncRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // State for product interactions
  const [quantity, setQuantity] = useState(1);
  const [cartError, setCartError] = useState<string | null>(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartSuccess, setCartSuccess] = useState(false);
  const [mainImage, setMainImage] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState('description');

  // Variant selection state
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedSku, setSelectedSku] = useState<VariantSku | null>(null);

  // State for recommended products
  const [recommendedProducts, setRecommendedProducts] = useState<any[]>([]);
  const [recommendedLoading, setRecommendedLoading] = useState(true);

  // Fetch product data
  useEffect(() => {
    if (!productId) {
      setError('Product ID is required');
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: rawData, error: fetchError } = await supabase.rpc(
          'fetch_product_with_images_by_id_v2',
          { p_product_id: parseInt(productId) }
        );

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Product not found');
          } else {
            throw fetchError;
          }
          return;
        }

        // v2 returns an array — take first element
        const detailedProduct: Product | undefined = Array.isArray(rawData)
          ? rawData[0]
          : rawData;

        if (!detailedProduct) {
          setError('Product not found');
          return;
        }

        setProduct(detailedProduct);

        // Pre-select default SKU for variant products
        if (detailedProduct.has_variants && detailedProduct.variant_tree) {
          const defaultSku =
            detailedProduct.variant_tree.skus.find((s) => s.is_default) ??
            detailedProduct.variant_tree.skus[0];
          if (defaultSku) {
            setSelectedOptions({ ...defaultSku.options });
            setSelectedSku(defaultSku);
          }
        }

        if (
          Array.isArray(detailedProduct.image_paths) &&
          detailedProduct.image_paths.length > 0
        ) {
          setMainImage(getPrimaryProductImageUrl(detailedProduct.image_paths));
        } else {
          setMainImage('');
        }
      } catch (err) {
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Redirect numeric URLs to slug URLs (e.g. /product/1388 → /product/chanel-oud-royal-1388)
  useEffect(() => {
    if (product && slug && /^\d+$/.test(slug)) {
      navigate(buildProductUrl(product.id, product.name || '', (product as any).brand_name), { replace: true });
    }
  }, [product, slug]);

  // Fetch recommended products
  useEffect(() => {
    const fetchRecommended = async () => {
      if (!productId) return;
      setRecommendedLoading(true);
      try {
        const response = await getProductRecommendationsV2({
          product_id: Number(productId),
        });
        let recommended: any[] = [];
        if (
          response &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray((response as any).data)
        ) {
          recommended = (response as any).data;
        }
        setRecommendedProducts(recommended);
      } catch {
        setRecommendedProducts([]);
      } finally {
        setRecommendedLoading(false);
      }
    };
    fetchRecommended();
  }, [productId]);

  // Check if product is in wishlist
  useEffect(() => {
    const checkWishlist = async () => {
      if (!userId || !productId) return;
      try {
        const payload = { user_id: userId };
        const response = await getUserWishlistV2(payload);
        const items = response?.data?.items || [];
        setIsWishlisted(
          items.some((item: any) => item.id === Number(productId))
        );
      } catch (e) {
        setIsWishlisted(false);
      }
    };
    checkWishlist();
  }, [userId, productId]);

  // Variant option selection
  const handleOptionSelect = (typeName: string, optionValue: string) => {
    if (!product?.variant_tree) return;
    const newOptions = { ...selectedOptions, [typeName]: optionValue };
    setSelectedOptions(newOptions);
    const match = product.variant_tree.skus.find((sku) =>
      Object.entries(newOptions).every(([t, v]) => sku.options[t] === v)
    );
    setSelectedSku(match ?? null);
  };

  // Price calculations
  const getVariantStock = (): number => {
    if (product?.has_variants) {
      if (selectedSku?.is_unlimited_stock) return 99;
      return selectedSku?.quantity ?? 0;
    }
    if (product?.is_unlimited_stock) return 99;
    return product?.quantity ?? 0;
  };

  const originalPrice: number = product?.has_variants
    ? selectedSku?.usd_price ?? product?.min_variant_price ?? 0
    : product?.usd_price ?? 0;

  const effectiveSaleRate: number = product?.has_variants
    ? selectedSku?.sale_rate ?? 0
    : product?.sale_rate ?? 0;

  const discountedPrice: number =
    effectiveSaleRate > 0
      ? originalPrice * (1 - effectiveSaleRate / 100)
      : originalPrice;

  const hasDiscount = effectiveSaleRate > 0;

  // Handlers
  const handleQuantityChange = (type: 'increment' | 'decrement') => {
    setCartError(null);
    const stock = getVariantStock();
    if (type === 'increment') {
      if (quantity < stock) setQuantity(quantity + 1);
    } else {
      setQuantity(quantity > 1 ? quantity - 1 : quantity);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    // Require variant selection for variant products
    if (product.has_variants && !selectedSku) {
      setCartError('Please select all options before adding to cart.');
      return;
    }

    if (product.has_variants && !selectedSku?.is_unlimited_stock && selectedSku?.stock_status === 'out_of_stock') {
      setCartError('This option is currently out of stock.');
      return;
    }

    const variantId = product.has_variants ? selectedSku?.id : undefined;
    const isUnlimited = product.has_variants ? selectedSku?.is_unlimited_stock : product.is_unlimited_stock;
    const stock = getVariantStock();

    const cartItem = useCartStore.getState().items.find(
      (i) => i.product_id === product.id && i.variant_id === variantId
    );
    const alreadyInCart = cartItem?.quantity ?? 0;
    const remaining = stock - alreadyInCart;

    if (!isUnlimited && remaining <= 0) {
      setCartError('You already have the maximum available quantity in your cart.');
      return;
    }

    const toAdd = isUnlimited ? quantity : Math.min(quantity, remaining);

    addToCartStore(product as any, toAdd, variantId);
    setCartError(null);
    setQuantity(1);

    // Guest: keep local; DB sync deferred until login merge in StoreInitializer.
    if (!userId) {
      setCartSuccess(true);
      setTimeout(() => setCartSuccess(false), 2500);
      return;
    }

    setCartLoading(true);
    setCartSuccess(false);
    if (dbSyncRef.current) clearTimeout(dbSyncRef.current);
    dbSyncRef.current = setTimeout(async () => {
      const finalQty =
        useCartStore.getState().items.find(
          (i) => i.product_id === product.id && i.variant_id === variantId
        )?.quantity ?? toAdd;
      try {
        await addProductToCart({ productId: product.id, quantity: finalQty, variantId });
        setCartSuccess(true);
        setTimeout(() => setCartSuccess(false), 2500);
      } catch {
        setCartError('Failed to sync cart. Please refresh.');
      } finally {
        setCartLoading(false);
      }
    }, 300);
  };

  const handleWishlistClick = async () => {
    if (!user) {
      navigate('/registration');
      return;
    }
    if (!product) return;
    setWishlistLoading(true);
    try {
      if (isWishlisted) {
        await deleteUserWishlistV2({ user_id: userId, product_id: product.id });
        setIsWishlisted(false);
      } else {
        await addToWishlist(product.id);
        setIsWishlisted(true);
      }
      await refreshWishlist();
    } catch (e) {
      // Optionally show error
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleImageChange = (image: string) => {
    setMainImage(image);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };

  return {
    // Data
    product,
    loading,
    error,
    quantity,
    cartError,
    cartLoading,
    cartSuccess,
    mainImage,
    selectedTab,
    recommendedProducts,
    recommendedLoading,
    isWishlisted,
    wishlistLoading,
    user,

    // Computed values
    originalPrice,
    discountedPrice,
    hasDiscount,

    // Variant state
    selectedOptions,
    selectedSku,

    // Handlers
    handleQuantityChange,
    handleAddToCart,
    handleOptionSelect,
    handleWishlistClick,
    handleImageChange,
    handleTabChange,
    handleBack,
    handleBackToProducts,
  };
};
